import { ChainId } from '@pulsex/chains'
import { SerializedFarm } from '@pulsex/farms'
import chunk from 'lodash/chunk'
import { masterChefAbi } from 'config/abi/masterchef'
import { farmFetcher } from 'state/farms'
import { notEmpty } from 'utils/notEmpty'
import { publicClient } from 'utils/wagmi'
import { AbiStateMutability, ContractFunctionReturnType } from 'viem'
import { SerializedFarmConfig } from '../../config/constants/types'
import { getMasterChefAddress } from '../../utils/addressHelpers'

export const fetchMasterChefFarmPoolLength = async (chainId: number) => {
  try {
    const client = publicClient({ chainId })
    const poolLength = await client.readContract({
      abi: masterChefAbi,
      address: getMasterChefAddress(chainId),
      functionName: 'poolLength',
    })

    return Number(poolLength)
  } catch (error) {
    console.error('Fetch MasterChef Farm Pool Length Error: ', error)
    return 0
  }
}

const masterChefFarmCalls = (farm: SerializedFarm) => {
  const { pid, quoteToken } = farm
  const multiCallChainId = farmFetcher.isTestnet(quoteToken.chainId) ? ChainId.PULSECHAIN_TESTNET : ChainId.PULSECHAIN
  const masterChefAddress = getMasterChefAddress(multiCallChainId)
  const masterChefPid = pid

  return masterChefPid || masterChefPid === 0
    ? ([
        {
          abi: masterChefAbi,
          address: masterChefAddress,
          functionName: 'poolInfo',
          args: [masterChefPid],
        },
        {
          abi: masterChefAbi,
          address: masterChefAddress,
          functionName: 'totalAllocPoint',
        },
      ] as const)
    : ([null, null] as const)
}
export type PoolInfo = ContractFunctionReturnType<typeof masterChefAbi, AbiStateMutability, 'poolInfo'>
export type TotalAllocPoint = ContractFunctionReturnType<
  typeof masterChefAbi,
  AbiStateMutability,
  'totalAllocPoint'
>

export const fetchMasterChefData = async (
  farms: SerializedFarmConfig[],
  chainId: number,
): Promise<[PoolInfo | null, TotalAllocPoint | null][]> => {
  const masterChefCalls = farms.map((farm) => masterChefFarmCalls(farm))
  const chunkSize = masterChefCalls.flat().length / farms.length
  const masterChefAggregatedCalls = masterChefCalls
    .filter((masterChefCall) => masterChefCall[0] !== null && masterChefCall[1] !== null)
    .flat()
    .filter(notEmpty)

  const multiCallChainId = farmFetcher.isTestnet(chainId) ? ChainId.PULSECHAIN_TESTNET : ChainId.PULSECHAIN
  const client = publicClient({ chainId: multiCallChainId })
  const masterChefMultiCallResult = await client.multicall({
    contracts: masterChefAggregatedCalls,
    allowFailure: false,
  })

  const masterChefChunkedResultRaw = chunk(masterChefMultiCallResult, chunkSize)

  let masterChefChunkedResultCounter = 0
  return masterChefCalls.map((masterChefCall) => {
    if (masterChefCall[0] === null && masterChefCall[1] === null) {
      return [null, null]
    }
    const data = masterChefChunkedResultRaw[masterChefChunkedResultCounter] as [PoolInfo, TotalAllocPoint]
    masterChefChunkedResultCounter++
    return data
  })
}
